import {Spinner} from '@primer/react'
import {clsx} from 'clsx'
import {useMemo} from 'react'

import {ItemType} from '../../api/memex-items/item-type'
import type {ReactionEmotion} from '../../api/side-panel/contracts'
import {DraftEdit} from '../../api/stats/contracts/draft'
import {usePostStats} from '../../hooks/common/use-post-stats'
import type {MemexItemModel} from '../../models/memex-item-model'
import {useIssueContext} from '../../state-providers/issues/use-issue-context'
import styles from './body.module.css'
import {SidePanelComment} from './comment'
import {SidePanelLiveUpdate} from './live-update'

export const SidePanelBody: React.FC<{item: MemexItemModel; isLoading: boolean; fullHeight?: boolean}> = ({
  item,
  isLoading,
  fullHeight,
}) => {
  const {sidePanelMetadata, editIssue, reactToSidePanelItem} = useIssueContext()

  const {postStats} = usePostStats()

  const capabilities = useMemo(() => new Set(sidePanelMetadata.capabilities), [sidePanelMetadata])

  const onReact = capabilities.has('react')
    ? (reaction: ReactionEmotion, reacted: boolean, actor: string) => reactToSidePanelItem(reaction, reacted, actor)
    : undefined

  const onEdit = useMemo(
    () =>
      capabilities.has('editDescription')
        ? async (body: string) => {
            await editIssue({body})
            if (item.contentType === ItemType.DraftIssue)
              postStats({
                name: DraftEdit,
                memexProjectItemId: item.id,
              })
          }
        : undefined,
    [capabilities, editIssue, item.contentType, postStats, item.id],
  )

  // `description` is typed as required, but the API can return metadata without it
  // (see https://github.com/github/memex/issues/26571). Fall back to an empty
  // description so we never access `.editedAt`/`.body` on `undefined`.
  const description = sidePanelMetadata.description ?? {body: '', bodyHtml: ''}

  return (
    <div className={clsx(styles.Box, fullHeight && styles.fullHeightContainer)}>
      <SidePanelLiveUpdate />
      {isLoading ? (
        <div className={styles.Box_1}>
          <Spinner aria-label="Loading" />
        </div>
      ) : (
        <section className={clsx(fullHeight && styles.fullHeightBox)}>
          <h3 style={{position: 'absolute', clipPath: 'circle(0)'}}>Description</h3>
          <SidePanelComment
            key={item.itemId()}
            allowEmptyBody
            author={sidePanelMetadata.user}
            createdAt={new Date(sidePanelMetadata.createdAt)}
            editedAt={description.editedAt ? new Date(description.editedAt) : undefined}
            reactions={sidePanelMetadata.reactions ?? {}}
            description={description}
            onEdit={onEdit}
            onReact={onReact}
          />
        </section>
      )}
    </div>
  )
}
